import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  DollarSign,
  Coins,
  Users,
  Search,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import TimeRangePicker from './TimeRangePicker';
import {
  getPlatformCreditsRollup,
  type PlatformCreditsRollup,
  type TimeRangeValue,
  resolveTimeRange,
} from '../../services/aiUsageApi';
import { RubyLoader } from '../RubyLoader';

/**
 * CreditsTab — platform credits rollup (spec §8.8)
 *
 * Totals, top-10 tenants, recent purchases, tenant search.
 */
const CreditsTab: React.FC = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRangeValue>({ preset: '90d' });
  const [rollup, setRollup] = useState<PlatformCreditsRollup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = resolveTimeRange(timeRange);
        const res = await getPlatformCreditsRollup({
          preset: timeRange.preset,
          fromIso: r.fromIso,
          toIso: r.toIso,
        });
        setRollup(res);
      } catch (err: any) {
        setError(err?.message || 'Failed to load platform credits');
        setRollup(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [timeRange]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const v = search.trim();
    if (v) navigate(`/tenant/${v}?tab=pos-ai`);
  };

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold text-stone-900">Platform Credits</h2>
          <p className="text-[11px] text-stone-500">Rollup across all tenants on agenticpos</p>
        </div>
        <TimeRangePicker value={timeRange} onChange={setTimeRange} compact />
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700 flex items-start gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Failed to load platform credits</p>
            <p className="text-xs mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {loading ? (
        <RubyLoader variant="section" label="Loading…" className="py-10" />
      ) : !rollup ? null : (
        <>
          {/* Totals cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              icon={<DollarSign size={16} />}
              label="Credits Sold"
              value={`$${(rollup.totals.creditsSoldUsd ?? 0).toFixed(2)}`}
              sub={`${(rollup.totals.creditsSold ?? 0).toLocaleString()} credits`}
              color="emerald"
            />
            <StatCard
              icon={<Coins size={16} />}
              label="Consumed"
              value={`$${(rollup.totals.creditsConsumedUsd ?? 0).toFixed(2)}`}
              sub={`${(rollup.totals.creditsConsumed ?? 0).toLocaleString()} credits`}
              color="purple"
            />
            <StatCard
              icon={<TrendingUp size={16} />}
              label="COGS"
              value={`$${(rollup.totals.cogsUsd ?? 0).toFixed(2)}`}
              sub="Upstream spend"
              color="amber"
            />
            <StatCard
              icon={<TrendingUp size={16} />}
              label="Gross Margin"
              value={`$${(rollup.totals.grossMarginUsd ?? 0).toFixed(2)}`}
              sub={`${(rollup.totals.grossMarginPct ?? 0).toFixed(0)}%`}
              color="blue"
            />
          </div>

          <div className="bg-white border border-stone-200 rounded-xl p-3 text-xs text-stone-600 flex items-center justify-between">
            <div>
              <Users size={12} className="inline mr-1 -mt-0.5 text-stone-400" />
              Net outstanding credits on tenant balances:
            </div>
            <div className="font-semibold text-stone-900">
              {(rollup.totals.netOutstandingCredits ?? 0).toLocaleString()}
            </div>
          </div>

          {/* Top tenants */}
          <section className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-stone-900">Top 10 tenants by consumption</h3>
              <span className="text-[11px] text-stone-500">{rollup.topTenants.length} tenants</span>
            </div>
            {rollup.topTenants.length === 0 ? (
              <div className="p-6 text-center text-xs text-stone-500">No consumption in this range.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        Tenant
                      </th>
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        Balance
                      </th>
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        Consumed
                      </th>
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        Purchased
                      </th>
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        Margin
                      </th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {rollup.topTenants.map((t) => (
                      <tr key={t.tenantId} className="hover:bg-stone-50">
                        <td className="px-3 py-2.5">
                          <div className="text-sm text-stone-800">{t.tenantName || '—'}</div>
                          <div className="font-mono text-[11px] text-stone-400">{t.tenantId}</div>
                        </td>
                        <td className="px-3 py-2.5 text-sm text-stone-800 text-right">
                          {(t.balance ?? 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-stone-800 text-right">
                          {(t.consumed ?? 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-stone-800 text-right">
                          {(t.purchased ?? 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-emerald-700 text-right font-semibold">
                          ${(t.marginUsd ?? 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => navigate(`/tenant/${t.tenantId}?tab=pos-ai`)}
                            className="text-[11px] font-medium text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 min-h-[28px] px-2"
                          >
                            View <ArrowRight size={10} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Recent purchases */}
          <section className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-stone-100">
              <h3 className="text-sm font-semibold text-stone-900">Recent purchases</h3>
              <p className="text-[11px] text-stone-500">Last 50</p>
            </div>
            {rollup.recentPurchases.length === 0 ? (
              <div className="p-6 text-center text-xs text-stone-500">No purchases in this range.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        When
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        Tenant
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        Bundle
                      </th>
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        Credits
                      </th>
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-stone-500 uppercase tracking-wide">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {rollup.recentPurchases.map((p, i) => (
                      <tr key={i} className="hover:bg-stone-50">
                        <td className="px-3 py-2.5 font-mono text-[11px] text-stone-500 whitespace-nowrap">
                          {new Date(p.at).toLocaleString(undefined, {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-xs text-stone-800">{p.tenantName || '—'}</div>
                          <div className="font-mono text-[10px] text-stone-400">{p.tenantId}</div>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-stone-700">
                          {p.bundleLabel || '—'}
                          {p.bundleQuantity && p.bundleQuantity > 1 ? ` ×${p.bundleQuantity}` : ''}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-stone-800 text-right">
                          {(p.credits ?? 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-stone-800 text-right font-semibold">
                          ${(p.amountUsd ?? 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Per-tenant drill-down search */}
          <section className="bg-white border border-stone-200 rounded-xl shadow-sm p-4">
            <h3 className="text-sm font-semibold text-stone-900 mb-2">Per-tenant drill-down</h3>
            <p className="text-[11px] text-stone-500 mb-3">
              Search by tenant ID to jump to that tenant's POS AI Agents tab.
            </p>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1 relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Tenant ID…"
                  className="w-full pl-9 pr-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 text-sm min-h-[44px]"
                />
              </div>
              <button
                type="submit"
                disabled={!search.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 min-h-[44px]"
              >
                Open
                <ArrowRight size={14} />
              </button>
            </form>
          </section>
        </>
      )}
    </div>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  color: 'emerald' | 'purple' | 'amber' | 'blue';
}> = ({ icon, label, value, sub, color }) => {
  const palette: Record<string, string> = {
    emerald: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-rose-100 text-rose-600',
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
  };
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-stone-500 font-medium">{label}</p>
          <p className="text-2xl font-semibold text-stone-900 mt-1">{value}</p>
          <p className="text-[11px] text-stone-400 mt-0.5">{sub}</p>
        </div>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${palette[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default CreditsTab;
